import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";

import "./ConstructionUpdate.css";

import { useApiQuery, resolveFileUrl } from "../api/client";
import openWhatsApp from "../utils/sendWhatsapp";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const ConstructionUpdate = () => {
  const { ref, inView } = useInView({ threshold: 0.16, triggerOnce: true });

  const { data, isLoading, isError } = useApiQuery("/construction-update");

  const milestones = data?.milestones || [];
  const gallery = data?.photos || [];
  const lastUpdated = data ? formatDate(data.lastUpdated) : "";

  const overall =
    data?.overallProgress ??
    (milestones.length
      ? Math.round(milestones.reduce((acc, m) => acc + m.percent, 0) / milestones.length)
      : 0);

  return (
    <section className="cu" id="construction" ref={ref}>
      <div className="cu__container">
        {/* Header */}
        <motion.div
          className="cu__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="cu__kicker">CONSTRUCTION UPDATE</div>
          <h2 className="cu__title">Live progress from the site.</h2>
          <p className="cu__sub">
            We share regular updates with photos and milestone progress so
            buyers can track real work — not just promises.
          </p>
        </motion.div>

        {isLoading && <div className="cu__state">Loading construction update…</div>}
        {isError && <div className="cu__state">Couldn't load the latest update right now.</div>}

        {!isLoading && !isError && (
          <>
            {/* Top Row: Progress + Last update */}
            <div className="cu__top">
              <motion.div
                className="cu__progressCard"
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.05 }}
              >
                <div className="cu__progressHeader">
                  <div className="cu__progressTitle">
                    <TimelineRoundedIcon /> Overall Progress
                  </div>
                  <div className="cu__progressPct">{overall}%</div>
                </div>

                <div className="cu__bar">
                  <motion.div
                    className="cu__barFill"
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${overall}%` } : {}}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                </div>

                <div className="cu__progressMeta">
                  <span className="cu__pill">
                    <ConstructionRoundedIcon /> Active Work
                  </span>
                  {lastUpdated && (
                    <span className="cu__pill">
                      <CalendarMonthRoundedIcon /> Last updated: {lastUpdated}
                    </span>
                  )}
                </div>
              </motion.div>

              <motion.div
                className="cu__ctaCard"
                initial={{ opacity: 0, y: 14 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.12 }}
              >
                <div className="cu__ctaTitle">Want weekly updates?</div>
                <div className="cu__ctaSub">
                  We’ll share construction photos and milestone progress on WhatsApp
                  or email.
                </div>

                <button
                  onClick={() => openWhatsApp()}
                  className="cu__btn" type="button">
                  Subscribe for Updates <ArrowOutwardRoundedIcon />
                </button>
              </motion.div>
            </div>

            {/* Milestones */}
            <motion.div
              className="cu__milestones"
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.18 }}
            >
              <div className="cu__panelHead">
                <div className="cu__panelKicker">MILESTONES</div>
                <div className="cu__panelTitle">Progress by phase</div>
              </div>

              {milestones.length === 0 ? (
                <div className="cu__state">No milestones added yet.</div>
              ) : (
                <div className="cu__milestoneGrid">
                  {milestones.map((m) => (
                    <div className="cu__mile" key={m._id}>
                      <div className="cu__mileTop">
                        <div className="cu__mileTitle">{m.title}</div>
                        <div
                          className={`cu__status ${m.status.replace(" ", "-").toLowerCase()}`}
                        >
                          <TaskAltRoundedIcon /> {m.status}
                        </div>
                      </div>

                      <div className="cu__mileBar">
                        <div
                          className="cu__mileFill"
                          style={{ width: `${m.percent}%` }}
                        />
                      </div>

                      <div className="cu__mileMeta">
                        <span>{m.percent}%</span>
                        <span className="cu__muted">Updated recently</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Site Photos Slider */}
            <motion.div
              className="cu__photos"
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.24 }}
            >
              <div className="cu__panelHead">
                <div className="cu__panelKicker">SITE PHOTOS</div>
                <div className="cu__panelTitle">Latest visuals from the ground</div>
              </div>

              {gallery.length === 0 ? (
                <div className="cu__state">No site photos posted yet.</div>
              ) : (
                <Swiper
                  modules={[Pagination, Autoplay]}
                  pagination={{ clickable: true }}
                  autoplay={{ delay: 2600, disableOnInteraction: false }}
                  spaceBetween={12}
                  slidesPerView={1}
                  breakpoints={{
                    720: { slidesPerView: 2 },
                    1100: { slidesPerView: 3 },
                  }}
                  className="cu__swiper"
                >
                  {gallery.map((g) => (
                    <SwiperSlide key={g._id}>
                      <div className="cu__photoCard">
                        <div className="cu__photoImgWrap">
                          <img src={resolveFileUrl(g.imageUrl)} alt={g.label} className="cu__photoImg" />
                        </div>
                        <div className="cu__photoLabel">{g.label}</div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default ConstructionUpdate;
